import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { clientsApi } from '../../api/clients.api';
import { projectsApi } from '../../api/projects.api';
import { sitesApi } from '../../api/sites.api';
import type { Oms, CreateOmsInput } from '../../api/oms.api';

interface Props {
  open: boolean;
  initial?: Oms | null;
  onClose: () => void;
  onSubmit: (input: CreateOmsInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateOmsInput = { omsNumber: '', title: '', clientId: 0, projectId: 0 };

export function OmsFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const mappingLocked = Boolean(initial?.isLocked);
  const [form, setForm] = useState<CreateOmsInput>(empty);

  const { data: clients } = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 100, status: 'active' }), enabled: open });
  const { data: projects } = useQuery({ queryKey: ['projects', 'byClient', form.clientId], queryFn: () => projectsApi.list({ limit: 100, clientId: form.clientId }), enabled: open && !!form.clientId });
  const { data: sites } = useQuery({ queryKey: ['sites', 'byProject', form.projectId], queryFn: () => sitesApi.list({ limit: 100, projectId: form.projectId }), enabled: open && !!form.projectId });

  useEffect(() => {
    setForm(
      initial
        ? {
            omsNumber: initial.omsNumber,
            title: initial.title ?? '',
            description: initial.description ?? undefined,
            clientId: initial.clientId ?? 0,
            projectId: initial.projectId ?? 0,
            siteId: initial.siteId,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateOmsInput>(k: K, v: CreateOmsInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit OMS — ${initial?.omsNumber}` : 'Create OMS'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          {mappingLocked && <Alert severity="info">Mapping is locked — a transaction is already linked to this OMS.</Alert>}
          <TextField
            label="OMS ID"
            placeholder="OMS-2026-000001"
            value={form.omsNumber}
            onChange={(e) => set('omsNumber', e.target.value.toUpperCase())}
            disabled={isEdit}
            helperText={isEdit ? 'OMS ID cannot be changed' : 'Manually entered — format OMS-YYYY-NNNNNN'}
            required
            fullWidth
          />
          <TextField label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} required fullWidth />
          <TextField label="Description" value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} fullWidth multiline rows={2} />
          <TextField select label="Client" value={form.clientId || ''} onChange={(e) => set('clientId', Number(e.target.value))} disabled={mappingLocked} required fullWidth>
            {(clients?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName} ({c.clientCode})</MenuItem>)}
          </TextField>
          <TextField select label="Project" value={form.projectId || ''} onChange={(e) => set('projectId', Number(e.target.value))} disabled={mappingLocked || !form.clientId} required fullWidth>
            {(projects?.data ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.projectName} ({p.projectCode})</MenuItem>)}
          </TextField>
          <TextField select label="Site (optional)" value={form.siteId ?? ''} onChange={(e) => set('siteId', e.target.value ? Number(e.target.value) : null)} disabled={mappingLocked || !form.projectId} fullWidth>
            <MenuItem value="">— None —</MenuItem>
            {(sites?.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.siteName} ({s.siteCode})</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)} disabled={!form.omsNumber || !form.title || !form.clientId || !form.projectId}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
